import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useImpersonation } from './ImpersonationContext';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Custom hook that considers impersonation - this should be used by components that need user data
export function useEffectiveUser() {
  const { currentUser } = useAuth();
  const { impersonatedUser, isImpersonating } = useImpersonation();
  
  // When impersonating, we need to create a user-like object with the impersonated user's ID
  const effectiveUser = useMemo(() => {
    return isImpersonating && impersonatedUser ? {
      uid: impersonatedUser.id, // Map the Firestore document ID to uid for Firebase compatibility
      email: impersonatedUser.email,
      displayName: impersonatedUser.username,
      emailVerified: true // Assume impersonated users are verified
    } : currentUser;
  }, [isImpersonating, impersonatedUser, currentUser]);
  
  return {
    currentUser: effectiveUser, // This is what components should use
    realUser: currentUser,      // The actual Firebase user (admin)
    isImpersonating,
    impersonatedUser
  };
}